import { LANGUAGES } from './consts';

export interface DashboardState {
    isLoading: boolean;
    upcomingEvents: EventType[];
}

export interface RegisterPayload {
    name: string;
    surname: string;
    email: string;
    password: string;
    countryCode: string;
    city: string;
    language: LANGUAGES;
}

export interface GoogleAuthNeedsProfileResponse {
    needsProfile: true;
    missing: string[];
}

export interface GoogleAuthCompleteResponse {
    needsProfile: false;
}

export type GoogleAuthResponse =
    | GoogleAuthNeedsProfileResponse
    | GoogleAuthCompleteResponse;

export interface CompleteRegisterPayload {
    name?: string;
    surname?: string;
    countryCode: string;
    city: string;
    language: LANGUAGES;
}

export interface CompleteUserProfilePayload {
    countryCode: string | null;
    city: string | null;
}

export interface UpdateInfoPayload {
    country: string | null;
    city: string | null;
    language: LANGUAGES | null;
}

export interface ContactUsPayload {
    email: string | null;
    title: string | null;
    message: string | null;
}

export interface AuthResponse {
    data: Auth;
    token: string;
}

export interface Auth {
    id: string;
    name: string;
    surname: string;
    username: string;
    avatarUrl: string | null;
    language: LANGUAGES;
    city: City;
    country: Country;
    countryCode: string;
    currency: string;
    stats: Stats;
    createdAt: string;
    email: string;
    notifications: INotification[];
    reportCount: number;
    lastReportedDate: string;
    userFollowers: number;
    isFollowing: boolean;
    averageRating: number;
    ratingCount: number;
}

export interface AuthState {
    isLoading: boolean;
    meIsLoading: boolean;
    error: any;
    authData: Auth | null;
    wasUserFetched: boolean;
}

export interface City {
    id: string;
    countryCode: string;
    name: string;
}

export interface Country {
    id: string;
    code: string;
    currency: string;
}

export interface GeoDataState {
    isLoading: boolean;
    error: any;
    cities: City[] | null;
}

export interface NotificationState {
    isLoading: boolean;
    error: any;
    notifications: INotification[];
}

export interface INotification {
    status: NotificationStatus;
    id: string;
    sender: Auth;
    text: string;
    date: string;
    type: NotificationTypes;
    sportEvent?: {
        id: string;
        name: string;
    };
}

export enum NotificationStatus {
    UNREAD = 'UNREAD',
    READ = 'READ',
}

export enum NotificationTypes {
    JOIN = 'JOIN',
    LEAVE = 'LEAVE',
    JOIN_REQUEST = 'JOIN_REQUEST',
    JOIN_REQUEST_ACCEPT = 'JOIN_REQUEST_ACCEPT',
    JOIN_REQUEST_DECLINE = 'JOIN_REQUEST_DECLINE',
    START_VOTING_PHASE = 'START_VOTING_PHASE',
    CLOSE_EVENT = 'CLOSE_EVENT',
    CANCEL = 'CANCEL',
    CANCELLED_BY_SYSTEM = 'CANCELLED_BY_SYSTEM',
    INVITATION = 'INVITATION',
    INVITATION_ACCEPT = 'INVITATION_ACCEPT',
    INVITATION_DECLINE = 'INVITATION_DECLINE',
    FOLLOW = 'FOLLOW',
}

export enum EVENT_TYPES {
    FIVE_SIDE_FOOTBALL = 'FIVE_SIDE_FOOTBALL',
    SEVEN_SIDE_FOOTBALL = 'SEVEN_SIDE_FOOTBALL',
    BASKETBALL = 'BASKETBALL',
    THREE_SIDE_VOLLEYBALL = 'THREE_SIDE_VOLLEYBALL',
    FIVE_SIDE_VOLLEYBALL = 'FIVE_SIDE_VOLLEYBALL',
}

export enum EVENT_STATUSES {
    OPEN = 'OPEN',
    VOTE_PHASE = 'VOTE_PHASE',
    CANCELLED = 'CANCELLED',
    CANCELLED_BY_SYSTEM = 'CANCELLED_BY_SYSTEM',
    CLOSED = 'CLOSED',
}

export interface EventType {
    id: string;
    type: EVENT_TYPES;
    maxUsers: number;
}

export interface NecessaryDataState {
    isLoading: boolean;
    data: NecessaryData | null;
}

export interface EventSearchFiltersState {
    countryCode: string;
    city: string;
    eventType: string | null;
    startDate: string | null;
}

export interface UserLevelState {
    levels: number[];
}

export interface NecessaryData {
    eventTypes: EventType[];
}

export interface OptionType {
    id: number | string;
    label: string;
    image?: string | null;
    averageRating?: number;
}

export interface CountryOptionType {
    id: string;
}

export interface ITeam {
    id: string;
    name: string;
    users: TeamUser[];
}

export interface TeamUser {
    id: string;
    username: string;
    name: string;
    surname: string;
    avatarUrl: string | null;
    hasVoted: boolean;
    averageRating: number;
}

export interface UnpopulatedTeam {
    id: string;
    users: string[];
}

export interface IJoinRequest {
    id: string;
    status: JOIN_REQUEST_STATUS;
    user: Auth;
}

export interface ILike {
    user: string;
    likesCount: number;
}

export interface IReport {
    count: number;
    lastReportDate: Date;
}

export interface IBoardMessage {
    id: string;
    user: Auth;
    date: string;
    message: string;
}

export interface IEvent {
    id: string;
    name: string;
    owner: Auth;
    status: EVENT_STATUSES;
    isPrivate: boolean;
    isPartial: boolean;
    place: string;
    address: string;
    mapsUrl: string;
    ownerNotes: string;
    type: EventTypes;
    joinedUsers: number;
    maxUsers: number;
    date: string;
    duration: number;
    city: string;
    totalPrice: number;
    currency: string;
    teams: ITeam[];
    joinRequests: IJoinRequest[];
    voteEndDate: string | null;
    likes: ILike[];
    reports: string[];
    winner: string | null;
    voters: string[];
    mvp: string;
    invitations: Invitation[];
    userRatings: UserRatingResponse[];
}

export interface UnpopulatedEvent {
    id: string;
    status: EVENT_STATUSES;
    name: string;
    owner: Auth;
    isPrivate: boolean;
    place: string;
    address: string | null;
    mapsUrl: string | null;
    type: EventTypes;
    joinedUsers: number;
    maxUsers: number;
    date: string;
    city: string;
    totalPrice: number;
    currency: string;
    teams: UnpopulatedTeam[];
    joinRequests: IJoinRequest[];
    voteEndDate: string | null;
    likes: ILike[];
    winner: string | null;
    voters: string[];
    mvp: string;
}

export interface Stats {
    eventsJoined: number;
    eventsWon: number;
    eventsLost: number;
    totalPrice: number;
    currency: string;
    likesReceived: number;
    dislikeReceived: number;
    mvpReceived: number;
}

export interface UserSearchResult {
    id: string;
    username: string;
    avatarUrl: string | null;
}

export interface Invitation {
    user: {
        id: string;
        avatarUrl: string | null;
        username: string;
        averageRating: number;
    };
    creator: {
        id: string;
        avatarUrl: string | null;
        username: string;
        averageRating: number;
    };
}

export enum EventTypes {
    FOOTBALL = 'FOOTBALL',
    BASKETBALL = 'BASKETBALL',
    VOLLEYBALL = 'VOLLEYBALL',
    SQUASH = 'SQUASH',
    TENNIS = 'TENNIS',
}

export enum JOIN_REQUEST_STATUS {
    PENDING = 'PENDING',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
}

export interface Stats {
    likes: number;
    appearances: number;
    victories: number;
    defeats: number;
    loss: number;
    mvp: number;
    organized: number;
}

export interface Country {
    code: string;
    currency: string;
    name: string;
}

export interface User {
    id: string;
    username: string;
    name: string;
    surname: string;
    avatarUrl: string | null;
    city: City;
    country: Country;
    countryCode: string;
    currency: string;
    stats: Stats;
    createdAt: string;
    email: string;
    notifications: INotification[];
    reportCount: number;
    lastReportedDate: string;
    userFollowers: number;
    isFollowing: boolean;
    averageRating: number;
    ratingCount: number;
}

export enum EventHistoryTypes {
    EVENT_JOIN = 'EVENT_JOIN',
    EVENT_LEAVE = 'EVENT_LEAVE',
    // TODO: EVENT_WIN ??
}

export interface CountriesResponse {
    id: string;
    code: string;
    name: string;
    currency: string;
}

export interface CitiesResponse {
    id: string;
    name: string;
    countryCode: string;
}

export interface CreateEventPayload {
    name: string;
    type: string;
    place: string;
    address: string | null;
    mapsUrl: string | null;
    totalPrice: number;
    date: string;
    isPrivate: boolean;
    maxUsers: number;
    duration: number;

    ownerNotes: string | null;
}

export enum UserReportTypes {
    NO_SHOW = 'NO_SHOW',
    MISCONDUCT = 'MISCONDUCT',
}

export interface UserRating {
    userId: string;
    rating: number | null;
}

export interface UserRatingResponse {
    userId: string;
    avgRating: number;
}
